import { Fragment } from "react";
import { styled } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { SidePanelContainer, SidePanelHeader, SidePanelSection, DateColumn, DatedListDivider } from "../ui";
import { Flex, Chip } from "../../primitives";
import { usePeriodLabel } from "../../DateRangeSelector/DateRangeSelector";
import { PeriodOption, DateRange } from "../../DateRangeSelector/types";
import { periodToDateRange } from "../../../utils/periodToDateRange";
import { useSingleEmployee } from "@acto/taskpane/core/data-access/team/useSingleEmployee";
import { useDismissedCustomersData } from "@acto/taskpane/core/data-access/team/useDismissedCustomersData";
import { MANAGEMENT_TODO_TYPES } from "../../../utils/managementTodoTypes";
import type { DismissalAnswer } from "@acto/taskpane/core/domain/TeamEmployee";
import { DismissedCustomersSidePanelSkeleton } from "./DismissedCustomersSidePanelSkeleton";

interface DismissedCustomersSidePanelProps {
  employeeId: string;
  employeeName: string;
  selectedPeriod?: PeriodOption;
  selectedCustomDateRange?: DateRange;
  onCustomerClick?: (customerId: string, customerName: string) => void;
}

const DismissalCard = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.managementView.spacing[16],
  alignItems: "start",
  textAlign: "left",
  padding: theme.managementView.spacing[12],
  borderRadius: theme.managementView.radius[10],
  marginLeft: `-${theme.managementView.spacing[12]}`,
}));

const DismissalTitle = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.managementView.spacing[8],
  width: "100%",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[90],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  flex: 1,
  textAlign: "left",

  "&[data-clickable='true']": {
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const ClientExternalReference = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[56],
}));

const SignalType = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[56],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  textAlign: "left",
}));

const DetailsList = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: `${theme.managementView.spacing[4]} ${theme.managementView.spacing[12]}`,
  alignItems: "center",
}));

const DetailItem = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[90],
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[6],
}));

const Answer = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[56],
  textAlign: "left",
}));

export const DismissedCustomersSidePanel = ({
  employeeId,
  employeeName,
  selectedPeriod = PeriodOption.LAST_MONTH,
  selectedCustomDateRange,
  onCustomerClick,
}: DismissedCustomersSidePanelProps) => {
  const t = useTranslations();

  const answerText = ({ reason, comment }: DismissalAnswer) => {
    const reasonLabel = reason
      ? t(`managementView.ignoredSignals.reason.${reason}`)
      : t("managementView.ignoredSignals.reasonNotAsked");
    return comment ? `${reasonLabel} · ${comment}` : reasonLabel;
  };

  const effectiveDateRange = periodToDateRange(selectedPeriod, selectedCustomDateRange);
  const periodLabel = usePeriodLabel(selectedPeriod, selectedCustomDateRange);

  const { employee, isPending, isFetching } = useSingleEmployee({
    employeeId,
    dateRange: effectiveDateRange,
    todoTypes: MANAGEMENT_TODO_TYPES,
  });

  const data = useDismissedCustomersData(employee?.dismissals ?? null, employeeName, periodLabel);

  const openCustomer = (dismissal: {
    clientId: string | null;
    companyName: string | null;
    clientAccessible: boolean;
  }) => {
    const { clientId, companyName } = dismissal;
    return onCustomerClick && dismissal.clientAccessible && clientId != null && companyName != null
      ? () => onCustomerClick(clientId, companyName)
      : undefined;
  };

  if (isPending || isFetching) {
    return <DismissedCustomersSidePanelSkeleton />;
  }

  return (
    <SidePanelContainer>
      <SidePanelHeader
        title={t("managementView.ignoredSignals.notInterestedTitle")}
        subtitle={`${data.employeeName} • ${data.dateRange}`}
      />

      <SidePanelSection title={`${data.ignoredCount} ${t("managementView.ignoredSignals.notInterested")}`}>
        <Flex direction="column">
          {data.dismissals.map((dismissal, index) => {
            const handleOpenCustomer = openCustomer(dismissal);
            return (
              <Fragment key={dismissal.id}>
                <DismissalCard>
                  <DateColumn>
                    <Chip size="large" color="default">
                      {dismissal.date}
                    </Chip>
                  </DateColumn>
                  <Flex direction="column" align="start" gap={24}>
                    <Flex direction="column" gap={2} align="start" style={{ width: "100%" }}>
                      <DismissalTitle data-clickable={Boolean(handleOpenCustomer)} onClick={handleOpenCustomer}>
                        {dismissal.companyName ?? t("managementView.deletedClient")}
                      </DismissalTitle>
                      {dismissal.clientExternalReference && (
                        <ClientExternalReference>#{dismissal.clientExternalReference}</ClientExternalReference>
                      )}
                      {dismissal.signalType && <SignalType>{dismissal.signalType}</SignalType>}
                    </Flex>
                    <DetailsList>
                      {dismissal.details.location && <DetailItem>{dismissal.details.location}</DetailItem>}
                      {dismissal.details.industry && <DetailItem>{dismissal.details.industry}</DetailItem>}
                      {dismissal.details.visitDate && <DetailItem>{dismissal.details.visitDate}</DetailItem>}
                      <DetailItem>{dismissal.details.expiryDate}</DetailItem>
                    </DetailsList>
                    {dismissal.answer && <Answer>{answerText(dismissal.answer)}</Answer>}
                  </Flex>
                </DismissalCard>
                {index < data.dismissals.length - 1 && <DatedListDivider />}
              </Fragment>
            );
          })}
        </Flex>
      </SidePanelSection>
    </SidePanelContainer>
  );
};
